import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { useLiveReviews } from "../hooks/useLiveReviews";
import { useT } from "../i18n/LanguageContext";

export default function ReviewsSlideshow() {
  const tr = useT();
  const { items, rating, placeUrl, live } = useLiveReviews();
  const [i, setI] = useState(0);

  const next = useCallback(() => setI((p) => (p + 1) % items.length), [items.length]);
  const prev = () => setI((p) => (p - 1 + items.length) % items.length);

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  const r = items[i];
  if (!r) return null;

  return (
    <section className="relative px-6 md:px-12 py-24 md:py-32 bg-[#121212] grain overflow-hidden" data-testid="reviews-section">
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <p className="overline mb-3" data-testid="reviews-rating">{tr("reviews.overline")} · {rating}</p>
        <div className="flex justify-center gap-1 mb-10">
          {[...Array(5)].map((_, k) => (
            <Star key={k} size={18} className="fill-[#C5A059] text-[#C5A059]" />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
            data-testid="review-slide"
          >
            <p className="font-serif text-2xl md:text-4xl leading-snug text-[#F5F5F0] font-light">
              “{r.text}”
            </p>
            <div className="mt-8">
              <p className="text-[#C5A059] tracking-wide">{r.name}</p>
              {r.sub && <p className="text-white/40 text-sm mt-1">{r.sub}</p>}
              {r.url && (
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="review-source-link"
                  className="inline-flex items-center gap-1.5 text-white/40 hover:text-[#C5A059] text-xs mt-3 transition-colors"
                >
                  {tr("reviews.viewOnGoogle")} <ExternalLink size={12} />
                </a>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-center gap-6 mt-12">
          <button data-testid="review-prev" onClick={prev} className="text-white/50 hover:text-[#C5A059] transition-colors" aria-label="Previous">
            <ChevronLeft size={28} />
          </button>
          <div className="flex gap-2">
            {items.map((it, k) => (
              <button
                key={`${it.name}-${k}`}
                onClick={() => setI(k)}
                aria-label={`Review ${k + 1}`}
                className={`h-1.5 transition-all duration-300 ${k === i ? "w-8 bg-[#C5A059]" : "w-1.5 bg-white/20"}`}
              />
            ))}
          </div>
          <button data-testid="review-next" onClick={next} className="text-white/50 hover:text-[#C5A059] transition-colors" aria-label="Next">
            <ChevronRight size={28} />
          </button>
        </div>

        {live && placeUrl && (
          <a
            href={placeUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-google-link"
            className="inline-block mt-10 text-white/40 hover:text-[#C5A059] text-xs tracking-wide transition-colors"
          >
            {tr("reviews.providedByGoogle")}
          </a>
        )}
      </div>
    </section>
  );
}
